import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Search } from 'lucide-react'
import { categoriesOf, useStorefrontProducts } from '@/lib/useStorefrontCategories'
import { money } from '@/lib/money'
import ProductImage from '@/components/order/ProductImage'

const MAX_RESULTS = 8

/**
 * Top-anchored search palette over the public catalog. Filtering is
 * client-side on the cached storefront products query - categories are
 * free-text on products, so there is no backend search endpoint to call.
 * Arrow-key highlight of rows is a possible later refinement.
 */
export default function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const products = useStorefrontProducts()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [q, setQ] = useState('')

  const needle = q.trim().toLowerCase()
  const results = needle
    ? (products.data ?? [])
        .filter((p) => p.name.toLowerCase().includes(needle) || p.category.toLowerCase().includes(needle))
        .slice(0, MAX_RESULTS)
    : []

  // Fresh query and focused input on every open. autoFocus is unreliable
  // inside a portal plus AnimatePresence tree, so focus imperatively.
  useEffect(() => {
    if (!open) return
    setQ('')
    inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const openFirstResult = () => {
    const first = results[0]
    if (!first) return
    onClose()
    navigate(`/p/${first.id}`)
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 bg-foreground/50 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose()
          }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            className="mx-auto mt-[10vh] w-full max-w-xl rounded-2xl border border-foreground/10 bg-background shadow-xl"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
          >
            <div className="flex items-center gap-3 border-b border-foreground/10 px-5 py-4">
              <Search className="size-5 shrink-0 text-foreground/40" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') openFirstResult()
                }}
                placeholder="Search products..."
                aria-label="Search products"
                className="w-full bg-transparent text-base outline-none placeholder:text-foreground/40"
              />
              <span className="shrink-0 rounded border border-foreground/20 px-1.5 py-0.5 text-2xs text-foreground/50">
                Esc
              </span>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-3 scrollbar-thin">
              {needle === '' ? (
                <div className="p-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-foreground/50">Browse categories</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {categoriesOf(products.data).map((c) => (
                      <Link
                        key={c.name}
                        to={`/products?category=${encodeURIComponent(c.name)}`}
                        onClick={onClose}
                        className="rounded-lg border border-foreground/20 px-3 py-1.5 text-sm font-medium text-foreground/70 transition-colors hover:bg-foreground/5"
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/products"
                    onClick={onClose}
                    className="mt-4 inline-block text-sm font-medium text-foreground/70 hover:text-primary"
                  >
                    Browse all products
                  </Link>
                </div>
              ) : results.length === 0 ? (
                <div className="rounded-xl border border-dashed border-foreground/20 p-8 text-center text-sm text-foreground/60">
                  <p>No products match "{q.trim()}"</p>
                  <Link to="/products" onClick={onClose} className="mt-2 inline-block font-medium text-primary">
                    Browse all products
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-foreground/5">
                  {results.map((p) => (
                    <Link
                      key={p.id}
                      to={`/p/${p.id}`}
                      onClick={onClose}
                      className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-foreground/5"
                    >
                      <div className="size-12 shrink-0">
                        <ProductImage product={p} className="size-12" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{p.name}</p>
                        <p className="text-xs text-foreground/50">{p.category}</p>
                      </div>
                      <p className="shrink-0 text-sm font-bold text-primary">
                        {money(p.special_price ?? p.from_price)}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
